import {
  AdBreakInfo,
  AdComposedPlayer,
  AdInfo,
  BUFFERING_STATES,
  PLAYBACK_STATES,
  PlayerContext,
  SEEKING_STATES,
} from '@quickplay/player-interface';
import { createLogger, Logger, LoggerLevel } from '@quickplay/foundation';
import { adFrequencyEventPublisher } from './EventBus';
import { AdFrequencyEventPublisher } from './types';

interface AdState {
  duration?: number;
  watched: number;
  outcome?: 'completed' | 'skipped' | 'failed';
}

const DEFAULT_CONTENT_CONSUMPTION_REPORTING_INTERVAL_IN_MS = 180_000;

export interface AdFrequencyPlaybackTracker {

  /** Starts AFC lifecycle publication and content observation. */
  start(): void;

  /**
   * Publishes the valid content delta since the previous baseline.
   * Call this immediately before an opportunity decision so a pending
   * interval does not delay the decision.
   */
  syncContentConsumption(): void;

  /** Releases player subscriptions and publishes the session-ended event. */
  dispose(): void;
}

export interface AdFrequencyPlaybackTrackerOptions {

  /** Player whose content and ad lifecycle events are observed. */
  player: AdComposedPlayer;

  /** Interval in milliseconds at which content consumption is reported. */
  contentConsumptionReportingIntervalInMS?: number;

  /** Optional diagnostic logger. */
  logger?: Logger;
}

/**
 * Observes one logical playback session and translates valid playback changes
 * into synchronous AFC events.
 */
export class AdFrequencyPlaybackTrackerImpl implements AdFrequencyPlaybackTracker {
  private readonly contentConsumptionReportingIntervalInMS: number;
  private readonly player: AdComposedPlayer;
  private readonly publisher: AdFrequencyEventPublisher;
  private readonly logger: Logger;
  private readonly ads = new Map<string, AdState>();
  private breakId?: string;
  private breakDuration?: number;
  private started = false;
  private disposed = false;
  private buffering = false;
  private seeking = false;
  private initialBaselineInitialized = false;
  private baselinePosition?: number;
  private baselineClock?: number;
  private contentProgressInterval?: ReturnType<typeof setInterval>;

  constructor(options: AdFrequencyPlaybackTrackerOptions) {
    this.player = options.player;
    this.publisher = adFrequencyEventPublisher;
    this.logger = options.logger ?? createLogger(LoggerLevel.OFF, 'AFC');
    this.contentConsumptionReportingIntervalInMS = (options.contentConsumptionReportingIntervalInMS ?? DEFAULT_CONTENT_CONSUMPTION_REPORTING_INTERVAL_IN_MS) * 0.5;
    this.logger.log('[AFC] Playback tracker initialized');

    this.player.subscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.subscribe('bufferingstatechanged', this.onBufferingStateChanged);
    this.player.subscribe('seekingstatechanged', this.onSeekingStateChanged);
    this.player.subscribe('adbreakstart', this.onAdBreakStart);
    this.player.subscribe('adstart', this.onAdStart);
    this.player.subscribe('adprogressupdate', this.onAdProgress);
    this.player.subscribe('adend', this.onAdEnd);
    this.player.subscribe('adskip', this.onAdSkipped);
    this.player.subscribe('aderror', this.onAdError);
    this.player.subscribe('adbreakend', this.onAdBreakEnd);
    this.player.subscribe('progressupdate', this.onInitialProgressUpdate);
  }

  /**
   * Starts tracking once. Repeated calls are ignored after startup or disposal.
   */
  start(): void {
    if (this.started || this.disposed) return;
    this.started = true;
    this.logger.log('[AFC] Playback tracker started');
    this.publisher.publish({ type: 'session-started' });
  }

  /**
   * Commits the current content position without waiting for the periodic
   * sampling interval. This is used by opportunity evaluation and teardown.
   */
  syncContentConsumption(): void {
    if (this.disposed) return;
    this.recordContentConsumption(true);
  }

  /**
   * Stops tracking and commits one final valid content delta before cleanup.
   */
  dispose(): void {
    if (this.disposed) return;
    this.syncContentConsumption();
    this.disposed = true;
    this.logger.log('[AFC] Playback tracker disposed', {
      hadActiveBreak: this.breakId !== undefined,
    });
    this.player.unsubscribe('playbackstatechanged', this.onPlaybackStateChanged);
    this.player.unsubscribe('bufferingstatechanged', this.onBufferingStateChanged);
    this.player.unsubscribe('seekingstatechanged', this.onSeekingStateChanged);
    this.player.unsubscribe('progressupdate', this.onInitialProgressUpdate);
    this.player.unsubscribe('adbreakstart', this.onAdBreakStart);
    this.player.unsubscribe('adstart', this.onAdStart);
    this.player.unsubscribe('adprogressupdate', this.onAdProgress);
    this.player.unsubscribe('adend', this.onAdEnd);
    this.player.unsubscribe('adskip', this.onAdSkipped);
    this.player.unsubscribe('aderror', this.onAdError);
    this.player.unsubscribe('adbreakend', this.onAdBreakEnd);
    if (this.started) this.publisher.publish({ type: 'session-ended' });
    this.clearContentProgressInterval();
    this.resetContentBaseline();
    this.resetAdBreak();
  }

  private clock(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
  }

  private readonly onPlaybackStateChanged = (state: PLAYBACK_STATES): void => {
    if (state === PLAYBACK_STATES.STARTED) {
      this.start();
      this.startContentProgressInterval();
    } else {
      this.syncContentConsumption();
      this.clearContentProgressInterval();
      this.logger.log('[AFC] Playback stopped or paused; resetting content baseline', { state });
      this.resetContentBaseline();
    }
  };

  private readonly onBufferingStateChanged = (state: BUFFERING_STATES): void => {
    const buffering = state === BUFFERING_STATES.ACTIVE;
    if (buffering) {
      this.syncContentConsumption();
    }
    this.buffering = buffering;
    this.logger.log('[AFC] Buffering state changed', { state, active: this.buffering });
    if (this.buffering) this.resetContentBaseline();
  };

  private readonly onSeekingStateChanged = (state: SEEKING_STATES): void => {
    this.seeking = state === SEEKING_STATES.ACTIVE;
    this.logger.log('[AFC] Seeking state changed', { state, active: this.seeking });
    if (this.seeking) this.resetContentBaseline();
  };


  private readonly onInitialProgressUpdate = (): void => {
    // Initial progress update received; establish the baseline for content consumption.
    this.initializeInitialBaseline();
    // `STARTED` emitted from player doesn't signal actual playback has started;
    // the content may not have started progressing when the `STARTED` update is received from the player.
    // Therefore, we rely on the initial progress update to establish the baseline for content consumption to fix potential inaccuracies.
    this.startContentProgressInterval();
    this.player.unsubscribe('progressupdate', this.onInitialProgressUpdate);
  };

  private shouldSkipContentConsumption(force: boolean): boolean {
    return (
      !this.started ||
      this.disposed ||
      this.buffering ||
      this.seeking ||
      !this.isMainContent() ||
      // Forced snapshots are allowed for pause/teardown because the player state
      // may already have left STARTED when the final content position is reported.
      (!force && this.player.playerState !== PLAYBACK_STATES.STARTED)
    );
  }

  private readonly recordContentConsumption = (force = false): void => {
    if (this.shouldSkipContentConsumption(force)) {
      this.resetContentBaseline();
      return;
    }

    const position = Math.ceil(this.player.currentTime);
    const now = this.clock();
    if (!Number.isFinite(position) || position < 0) {
      this.resetContentBaseline();
      return;
    }

    if (this.baselinePosition !== undefined && this.baselineClock !== undefined) {
      // The content-time movement since the previous accepted snapshot.
      const positionDelta = position - this.baselinePosition;
      // Use the monotonic clock to measure real elapsed playback-observation time.
      const elapsed = Math.max(0, (now - this.baselineClock) / 1000);
      const rate = Number.isFinite(this.player.playbackRate) && this.player.playbackRate > 0
        ? this.player.playbackRate
        : 1;
      // Permit normal playback-rate movement with tolerance for delayed callbacks,
      // while rejecting seek jumps and other implausibly large position changes.
      const maxPlausible = Math.max(1, elapsed * rate * 2 + 1);

      if (positionDelta > 0 && positionDelta <= maxPlausible) {
        this.logger.log('[AFC] Content consumed', {
          duration: positionDelta,
          position,
          playbackRate: rate,
        });
        this.publisher.publish({ type: 'content-consumed', duration: positionDelta });
      } else if (positionDelta < 0 || positionDelta > maxPlausible) {
        this.logger.log('[AFC] Ignoring implausible content movement', {
          positionDelta,
          maxPlausible,
          position,
        });
        this.resetContentBaseline();
      }
    }

    this.baselinePosition = position;
    this.baselineClock = now;
  };

  private initializeInitialBaseline(): void {
    if (this.initialBaselineInitialized) return;

    const position = Number.isFinite(this.player.currentTime) && this.player.currentTime > 0
      ? this.player.currentTime
      : 0;
    const playbackRate = Number.isFinite(this.player.playbackRate) && this.player.playbackRate > 0
      ? this.player.playbackRate
      : 1;
    this.baselinePosition = 0;
    this.baselineClock = this.clock() - (position / playbackRate) * 1000;
    this.initialBaselineInitialized = true;
  }

  private startContentProgressInterval(): void {
    this.clearContentProgressInterval();
    this.recordContentConsumption();
    this.contentProgressInterval = setInterval(
      this.recordContentConsumption,
      this.contentConsumptionReportingIntervalInMS,
    );
    this.logger.log('[AFC] Content progress interval started', {
      intervalMs: this.contentConsumptionReportingIntervalInMS,
    });
  }

  private clearContentProgressInterval(): void {
    if (this.contentProgressInterval === undefined) {
      return;
    }
    clearInterval(this.contentProgressInterval);
    this.contentProgressInterval = undefined;
    this.logger.log('[AFC] Content progress interval cleared');
  }

  private readonly onAdBreakStart = (breakInfo: AdBreakInfo): void => {
    if (this.disposed) return;
    this.breakId = breakInfo?.adBreakID;
    this.breakDuration = this.toSeconds(breakInfo?.durationMs);
    this.ads.clear();
    this.logger.log('[AFC] Ad break started', {
      breakId: this.breakId,
      duration: this.breakDuration,
    });
    this.publishAdBreakProgress(false);
  };

  private readonly onAdStart = (adInfo: AdInfo): void => {
    if (this.disposed || !this.breakId || !adInfo?.adID) return;
    this.ads.set(adInfo.adID, {
      duration: this.toSeconds(adInfo.durationMs),
      watched: 0,
    });
    this.logger.log('[AFC] Ad started', { breakId: this.breakId, adId: adInfo.adID });
  };

  private readonly onAdProgress = (adInfo: AdInfo, currentTime: number): void => {
    if (
      this.disposed ||
      !this.breakId ||
      !adInfo?.adID ||
      !Number.isFinite(currentTime) ||
      currentTime < 0
    ) {
      return;
    }

    const ad = this.ads.get(adInfo.adID) ?? {
      duration: this.toSeconds(adInfo.durationMs),
      watched: 0,
    };
    ad.duration ??= this.toSeconds(adInfo.durationMs);
    if (currentTime <= ad.watched) return;
    ad.watched = currentTime;
    this.ads.set(adInfo.adID, ad);
    this.logger.log('[AFC] Ad progress', {
      breakId: this.breakId,
      adId: adInfo.adID,
      watched: currentTime,
    });
    this.publishAdBreakProgress(false);
  };

  private readonly onAdEnd = (adInfo: AdInfo): void => {
    this.setAdOutcome(adInfo, 'completed');
  };

  private readonly onAdSkipped = (adInfo: AdInfo): void => {
    this.setAdOutcome(adInfo, 'skipped');
  };

  private readonly onAdError = (adInfo?: AdInfo): void => {
    if (adInfo) this.setAdOutcome(adInfo, 'failed');
    else {
      const activeAd = Array.from(this.ads.entries()).find(([, ad]) => !ad.outcome);
      if (activeAd) activeAd[1].outcome = 'failed';
    }
  };

  private readonly onAdBreakEnd = (): void => {
    if (this.disposed || !this.breakId) return;
    const totalDuration = this.effectiveDuration();
    const watched = this.watchedDuration();
    this.logger.log('[AFC] Ad break ended', {
      breakId: this.breakId,
      watched,
      totalDuration,
    });
    this.publishAdBreakProgress(true, totalDuration === undefined && watched > 0 ? watched : totalDuration);
    this.resetAdBreak();
  };

  private setAdOutcome(adInfo: AdInfo | undefined, outcome: 'completed' | 'skipped' | 'failed'): void {
    if (!adInfo?.adID) return;
    const ad = this.ads.get(adInfo.adID);
    if (ad) ad.outcome = outcome;
    this.logger.log('[AFC] Ad outcome updated', {
      breakId: this.breakId,
      adId: adInfo?.adID,
      outcome,
    });
  }

  private publishAdBreakProgress(isComplete: boolean, totalDuration = this.effectiveDuration()): void {
    if (!this.breakId) return;
    this.logger.log('[AFC] Publishing ad-break progress', {
      breakId: this.breakId,
      watched: this.watchedDuration(),
      totalDuration,
      isComplete,
    });
    this.publisher.publish({
      type: 'ad-break-progressed',
      progress: {
        breakId: this.breakId,
        watched: this.watchedDuration(),
        totalDuration,
        isComplete,
      },
    });
  }

  private watchedDuration(): number {
    return Array.from(this.ads.values())
      .filter(ad => ad.outcome !== 'skipped' && ad.outcome !== 'failed')
      .reduce((total, ad) => total + ad.watched, 0);
  }

  private effectiveDuration(): number | undefined {
    if (this.breakDuration !== undefined) return this.breakDuration;
    const durations = Array.from(this.ads.values())
      .map(ad => ad.duration)
      .filter((duration): duration is number => duration !== undefined);
    return durations.length > 0 ? durations.reduce((total, duration) => total + duration, 0) : undefined;
  }

  private isMainContent(): boolean {
    return this.player.playerContext === PlayerContext.MAIN;
  }

  private resetContentBaseline(): void {
    this.baselinePosition = undefined;
    this.baselineClock = undefined;
  }

  private resetAdBreak(): void {
    this.breakId = undefined;
    this.breakDuration = undefined;
    this.ads.clear();
  }

  private toSeconds(milliseconds?: number): number | undefined {
    return milliseconds !== undefined && Number.isFinite(milliseconds) && milliseconds > 0
      ? milliseconds / 1000
      : undefined;
  }
}
